import {computed, reactive} from "vue";

export class ValidatorRuleSet {
    constructor(rules, options) {
        // typeof null is 'object', because... it is JavaScript
        if(!['array', 'object'].includes(typeof rules) || (rules === null)) {
            throw new Error('First parameter must an iterable entity like array or object!');
        }

        if(!['undefined', 'object'].includes(typeof options)) {
            throw new Error('Second parameter must an object, null or undefined!');
        }

        this.rules = rules;
        this.options = options;

        this.digestRules();
    }

    digestRules() {
        for(let key in this.rules) {
            if(typeof this.rules[key] === 'function') {
                this.rules[key] = this.rules[key]();
            }
        }
    }
}

function registerGetters(container, items) {
    Object.defineProperty(container, '$keyList', {
        get: () => {
            return Object.keys(items);
        }
    });

    Object.defineProperty(container, '$errors', {
        get: function() {
            let result = [];

            this.$keyList.forEach((key) => {
                result.push(... this[key].$errors);
            });

            return result;
        }
    });

    Object.defineProperty(container, '$valid', {
        get: function() {
            for(let key in this.$keyList) {
                let value = this.$keyList[key];
                if(!this[value].$valid) {
                    return false;
                }
            }

            return true;
        },
    });

    Object.defineProperty(container, '$validate', {
        value: function() {
            for(let key in this.$keyList) {
                this[this.$keyList[key]].$validate();
            }
        },
        writable: false
    });

    return container;
}

const glue = '.';
const generateErrorMessages = function(component, container, value, key, newKeyChain, index) {
    let validatorParams = value.rules[index].params(component);

    for(let key in validatorParams) {
        if(typeof validatorParams[key] === 'function') {
            validatorParams[key] = validatorParams[key]();
        }
    }

    container[key].$errors.push({
        $message: value.rules[index].message(),
        $params: {
            ...validatorParams,
            /*
             * Computed values in $messageParams are reactive
             * to localization changes.
             */
            ...value.options?.$messageParams,
            _keyChain: newKeyChain,
            _key: key,
        },
    });
};

function executedValidationIsFalse(rule, component, newValue, oldValue) {
    return rule.handler(component, newValue, oldValue) === false;
}

function skipIfTargetIsEmpty(target) {
    return target === null || target === undefined;
}

function watcherCreator(items, keyChain, component, container, options) {
    registerGetters(container, items);

    for(let key in items) {
        let value = items[key];
        let newKeyChain = [keyChain, key].filter((a) => a !== null).join(glue);

        container[key] ??= {};

        if(value instanceof ValidatorRuleSet) {
            container[key].$valid ??= true;
            container[key].$errors ??= [];
            container[key].$validate = () => {
                let valid = true;
                container[key].$errors.length = 0

                for(let index in value.rules) {
                    let target = component;
                    newKeyChain.split(glue).forEach((property) => {
                        // When properties set to null, like deleting a model
                        // Not 100% sure if this is the best solution, but works now... strange
                        if(skipIfTargetIsEmpty(target)) {
                            return;
                        }

                        target = target[property];
                    });

                    if(executedValidationIsFalse(value.rules[index], component, target, target)) {
                        valid = false;
                        container[key].$valid = false;
                        generateErrorMessages(component, container, value, key, newKeyChain, index);
                    }

                    if(!valid && !(value.options?.evaluateAll ?? options.evaluateAll ?? false)) {
                        break
                    }
                }

                container[key].$valid = valid;
            };

            /**
             * Notice: $nextTick is important here!
             * Vue.js cannot set watchers on undefined properties of the components and this code runs
             * before the data() method returns anything useful already, as such, the expected values for the watchers
             * are always undefined yet. So the watcher must be registered in the next cycle to make the magic happen!
             * Then it can access the provided data() values and keys in the component.
             */
            component.$nextTick(() => {
                component.$watch(newKeyChain, {
                    handler: (...args) => {
                        let valid = true;
                        container[key].$errors.length = 0;

                        for(let index in value.rules) {
                            if(executedValidationIsFalse(value.rules[index], component, ...args)) {
                                valid = false;
                                generateErrorMessages(component, container, value, key, newKeyChain, index);
                            }

                            if(!valid && !(value.options?.evaluateAll ?? options.evaluateAll ?? false)) {
                                break;
                            }
                        }

                        container[key].$valid = valid;
                    },
                    immediate: value.options?.immediate ?? options?.immediate ?? false,
                    once: value.options?.once ?? options?.once ?? false,
                    deep: value.options?.deep ?? options?.deep ?? false
                });
            });
        }
        else {
            watcherCreator(value, newKeyChain, component, container[key], options);
        }
    }
}

let $target;
/**
 Example:

 import {rules} from "../../../plugins/Validator.js";
 import defaultValidatorConfiguration from "../../../constants/defaultValidatorConfiguration.js";
 ...
 data() {return {
    newPost: {
         message: null
    },
    v$: this.$validator({
        newPost: {
            message: rules(
                {required, minLength: minLength(3), maxLength: maxLength(10)},
                {
                    immediate: true,
                    $messageParams: {
                        fieldName: computed(() => this.$t('html.forms.labels.inputFieldName'))
                    }
                }
            ),
        }
    }, defaultValidatorConfiguration)
 };}
 ...
 */
export default class {
    install(app, option) {
        this.option = option;
        $target = this;

        /**
         *  Returns a ComputedRef object and registers watchers into the current component.<br/>
         *  First argument must represent a similar structure, like in the data property or the watchers will
         *  not do anything.</br>
         *  Second argument is optional and can determine the rules behaviour globally in this validator observer.<br/>
         *  <b>Tested in options api only!</b>
         *
         * @type {function(Object, ?Object): ComputedRef}
         */
        app.config.globalProperties.$validator = this.callback;

        /**
         * Example:
         * import { inject } from 'vue';
         *
         * const Reverb = inject('Validator')
         */
        app.provide('Validator', this.callback);
    }

    /**
     * 'this' will reference the component, where it was called.
     *
     * @returns {computed}
     */
    callback(rules, configuration) {
        let container = computed(() => {
            return reactive({}); // Reactivity is important here!
        });

        watcherCreator(rules, null, this, container.value, Object.assign({}, $target.option, configuration));

        return container;
    }
}

/**
 * @typedef {Object} ValidatorOptions
 * @property {Boolean} immediate Vue.js watcher: Evaluate immediately when mounted
 * @property {Boolean} once Vue.js watcher: Evaluate once only
 * @property {Boolean} deep Vue.js watcher: deep watcher behaviour
 * @property {Boolean} evaluateAll Rule handler: Validate all rules or break out at the first invalid
 */

/**
 * Returns ValidatorRuleSet class with locally configurable options.
 * Configuration <i>\$messageParams {Object}</i> will be available for the <i>\$message</i> property<br/>
 * Use computed() values in <i>\$messageParams</i> to make them reactive, for example about
 * localization changes! For example:<br/>
 * <i>computed(() => this.$t('localization.key'))</i>
 *
 * @param {Object} rules
 * @param {?ValidatorOptions} configuration
 * @returns {ValidatorRuleSet}
 */
export function rules(rules, configuration = null) {
    return new ValidatorRuleSet(rules, configuration);
}
